import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Users, Briefcase, Wifi, Sparkles } from 'lucide-react';
import { fleet } from '../mockData';
import { useLanguage } from '../LanguageContext';

const FleetSection = () => {
  const { t } = useLanguage();
  
  // Translation mapping for vehicle features
  const translateFeature = (feature) => {
    const featureMap = {
      'Hybrid': t('fleet.vehicleFeatures.hybrid'),
      'Climate control': t('fleet.vehicleFeatures.climateControl'),
      'Comfortable seats': t('fleet.vehicleFeatures.comfortableSeats'),
      'WiFi': t('fleet.vehicleFeatures.wifi'),
      'Premium sound': t('fleet.vehicleFeatures.premiumSound'),
      'Extra space': t('fleet.vehicleFeatures.extraSpace'),
    };
    return featureMap[feature] || feature;
  };
  
  // Translation for categories
  const translateCategory = (categoryKey) => {
    return t(`fleet.categories.${categoryKey}`);
  };
  
  // Translation for badges
  const translateBadge = (badgeKey) => {
    return t(`fleet.badges.${badgeKey}`);
  };
  
  // Translation for luggage
  const translateLuggage = (luggageKey) => {
    return t(`fleet.luggage.${luggageKey}`);
  };
  
  return (
    <section id="fleet" className="py-24 bg-gradient-to-b from-gray-900 to-black">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">{t('fleet.title')}</h2>
          <p className="text-xl text-gray-400">{t('fleet.subtitle')}</p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {fleet.map((vehicle, index) => (
            <Card 
              key={vehicle.id}
              className="bg-gradient-to-br from-gray-900 via-gray-800 to-black border-2 border-gold/30 hover:border-gold transition-all duration-500 overflow-hidden group hover:shadow-2xl hover:shadow-gold/20"
            >
              <div className="relative h-64 overflow-hidden">
                <img 
                  src={vehicle.image} 
                  alt={vehicle.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 brightness-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                <div className="absolute top-4 right-4 flex flex-col gap-2">
                  <Badge className={`${vehicle.badgeKey === 'recommended' ? 'bg-gradient-to-r from-green-500 to-emerald-600' : 'bg-gradient-to-r from-gold to-amber-600'} text-black border-0 font-semibold`}>
                    {translateBadge(vehicle.badgeKey)}
                  </Badge>
                  <Badge className="bg-gray-900/80 text-white border border-gold/30">
                    {translateCategory(vehicle.categoryKey)}
                  </Badge>
                </div>
              </div>
              
              <CardHeader>
                <CardTitle className="text-3xl text-white flex items-center justify-between">
                  {vehicle.name}
                  <span className="text-xl text-gold/70 font-normal italic">{t('fleet.onRequest')}</span>
                </CardTitle>
              </CardHeader>
              
              <CardContent className="space-y-6">
                {/* Capacity */}
                <div className="flex items-center gap-6 text-gray-300">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-gold" />
                    <span>{vehicle.passengers} {t('fleet.passengers')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-gold" />
                    <span>{translateLuggage(vehicle.luggageKey)}</span>
                  </div>
                </div>
                
                {/* Features */}
                <div className="space-y-2">
                  <h4 className="text-white font-semibold flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-gold" />
                    {t('fleet.features')}
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    {vehicle.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-gray-300 text-sm">
                        <div className="w-1.5 h-1.5 bg-gold rounded-full"></div>
                        {translateFeature(feature)}
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FleetSection;
